import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { formatDateTime } from '@utils/format';
import { useForecastRun } from '../hooks';
import type { ForecastRun } from '../schema';

interface Props {
  open: boolean;
  row: ForecastRun | null;
  onClose: () => void;
}

function JsonBlock({
  title,
  data,
}: {
  title: string;
  data: Record<string, unknown>;
}) {
  const isEmpty = Object.keys(data).length === 0;
  return (
    <div className="rounded-lg border border-border bg-surface-muted/40 p-3">
      <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-foreground-muted">
        {title}
      </div>
      {isEmpty ? (
        <span className="text-[12.5px] text-border-strong">empty</span>
      ) : (
        <pre className="overflow-x-auto whitespace-pre-wrap break-words rounded bg-surface px-3 py-2 font-mono text-[11.5px] leading-relaxed text-foreground ring-1 ring-border">
          {JSON.stringify(data, null, 2)}
        </pre>
      )}
    </div>
  );
}

// Read-only dialog: pretty-prints the parameters + metadata JSONB blocks the
// pipeline writes per run. No editing — pipeline owns these.
export function ForecastRunDetailDialog({ open, row, onClose }: Props) {
  const detailQuery = useForecastRun(row?.id ?? null);
  const detail = detailQuery.data;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="forecast-run-detail-title"
    >
      <DialogTitle id="forecast-run-detail-title" sx={{ pb: 1 }}>
        Forecast run #{row?.id ?? '—'}
      </DialogTitle>
      <DialogContent dividers>
        {!detail ? (
          <Box className="flex items-center justify-center py-12">
            <CircularProgress size={28} />
          </Box>
        ) : (
          <div className="grid gap-3">
            <div className="grid grid-cols-2 gap-3 rounded-lg bg-surface-muted px-4 py-3 text-[12.5px]">
              <div>
                <div className="text-[10.5px] font-semibold uppercase tracking-wider text-foreground-muted">
                  Status
                </div>
                <div className="font-medium text-foreground">{detail.status}</div>
              </div>
              <div>
                <div className="text-[10.5px] font-semibold uppercase tracking-wider text-foreground-muted">
                  Metric
                </div>
                <div className="font-medium uppercase text-foreground">
                  {detail.metric}
                </div>
              </div>
              <div>
                <div className="text-[10.5px] font-semibold uppercase tracking-wider text-foreground-muted">
                  Started
                </div>
                <div className="font-medium tabular-nums text-foreground">
                  {formatDateTime(detail.startedAt)}
                </div>
              </div>
              <div>
                <div className="text-[10.5px] font-semibold uppercase tracking-wider text-foreground-muted">
                  Finished
                </div>
                <div className="font-medium tabular-nums text-foreground">
                  {detail.finishedAt
                    ? formatDateTime(detail.finishedAt)
                    : '— in progress'}
                </div>
              </div>
              <div className="col-span-2">
                <div className="text-[10.5px] font-semibold uppercase tracking-wider text-foreground-muted">
                  Horizons · Rows
                </div>
                <div className="font-medium tabular-nums text-foreground">
                  {detail.horizons.join(', ') || '—'}d ·{' '}
                  {detail.rowsInput ?? '—'} →{' '}
                  {detail.rowsFeatures ?? '—'} →{' '}
                  {detail.rowsForecast ?? '—'}
                </div>
              </div>
              {detail.errorMessage && (
                <div className="col-span-2">
                  <div className="text-[10.5px] font-semibold uppercase tracking-wider text-foreground-muted">
                    Error
                  </div>
                  <div className="break-words font-mono text-[12px] text-danger">
                    {detail.errorMessage}
                  </div>
                </div>
              )}
            </div>

            <JsonBlock title="Parameters" data={detail.parameters} />
            <JsonBlock title="Metadata" data={detail.metadata} />
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
